import { PromiseOnSteroids } from "@mp-commons/lib/utils/PromiseOnSteroids.js";
import { AdsController } from "@mp-commons/modules/ads/AdsController.js";
import { AdsEnrichedVideoContext } from "@mp-commons/modules/ads/AdsEnrichedVideoContext.js";
import { MediaControlOrigin } from "@mp-commons/modules/events/PlayerEvent.js";
import { VideoAbstractPlayerControls } from "@mp-commons/modules/player/video/VideoAbstractPlayerControls.js";
import { MediaIdOrPlaylistIndex, VideoContext, VideoPlayerControls, VideoPlayerElements } from "@mp-commons/modules/player/Player.js";
import { PlayerControlsConfig } from "@mp-commons/modules/player/PlayerControlsConfig.js";
import { CursoredPlaylist } from "@mp-commons/modules/playlist/CursoredPlaylist.js";
import { MegarideEvent, MegarideEventType } from "./events/MegarideEvent.js";
import { MegaridePlayerControls, MegaridePlayerGUI } from "./MegaridePlayer.js";

import { MegarideAdsEnrichedVideoContext } from "./MegarideWrappingVideoContext.js";
import { MediaId } from "@mp-commons/model/MediaInfo.js";
import { VideoInfoRawData } from "@mp-commons/model/video/VideoInfo.js";
import { isCastablePlayerTechFactory, VideoPlayerTechFactory } from "@mp-commons/modules/player/video/VideoPlayerTechFactory.js";
import { MediaAutoplayPolicy, MediaAudioOnPolicy } from "@mp-commons/config/MediaPlayerConfig.js";
import { isAnyUnmutable, UnmuteInfo, UserGestureUnmuter } from "@mp-commons/modules/unmuter/UserGestureUnmuter.js";

/**
 * Extends the base `PlayerControlsConfig` interface to provide additional configuration options
 * for Megaride player controls.
 *
 * @property autoplay - Specifies the autoplay policy for media playback.
 * @property loopPlaylist - Determines whether the playlist should loop when it reaches the end.
 * @property audioOnPolicy - Defines the policy for enabling audio during playback.
 */
export interface MegaridePlayerControlsConfig extends PlayerControlsConfig {

    autoplay: MediaAutoplayPolicy,
    loopPlaylist: boolean,
    audioOnPolicy: MediaAudioOnPolicy

}



/**
 * Implements the base controls for the Megaride video player, providing playlist navigation,
 * ad integration, mute/unmute logic, and GUI interaction. This class extends `VideoAbstractPlayerControls`
 * and serves as the foundation for Megaride-specific player controls.
 *
 * @typeParam PlayerType - The type representing the underlying video player implementation.
 * @typeParam PlaylistType - The playlist type, which must extend `CursoredPlaylist<VideoInfoRawData>`.
 *
 * @remarks
 * - Handles initialization and playback logic, including autoplay and audio policies.
 * - Integrates with ad controllers to manage ad playback and context enrichment.
 * - Manages mute/unmute state based on user gestures and configuration.
 * - Provides methods for playlist navigation (next, previous, restart) and error signaling.
 * - Interacts with the GUI to update player state and media elements.
 *
 * @param playerName - The name of the player instance.
 * @param playlist - The playlist containing media items.
 * @param factoryBuilder - A factory function to create the player tech implementation.
 * @param adsControllerPromise - A promise resolving to the ads controller.
 * @param gui - The GUI interface for player controls.
 * @param config - The configuration for player controls and policies.
 * @param unmuter - Handles user gesture-based unmute logic.
 * @param win - The window context for the player.
 *
 * @public
 */
export class MegaridePlayerControlsBaseImpl<PlayerType, PlaylistType extends CursoredPlaylist<VideoInfoRawData>>
    extends VideoAbstractPlayerControls<PlaylistType> implements MegaridePlayerControls {

    private currentContext: Promise<AdsEnrichedVideoContext>;

    private firstContextResolve: (ct: AdsEnrichedVideoContext) => void;

    private notStartedYet: boolean;

    protected futureAdController: Promise<AdsController>;

    protected initPromise: Promise<AdsEnrichedVideoContext>;

    private unmutePromise: Promise<UnmuteInfo>;

    constructor(
        playerName: string,
        playlist: PlaylistType,
        factoryBuilder: (controls: VideoPlayerControls) => VideoPlayerTechFactory<PlayerType>,
        adsControllerPromise: Promise<AdsController>,
        private readonly gui: MegaridePlayerGUI,
        public readonly config: MegaridePlayerControlsConfig,
        protected unmuter: UserGestureUnmuter,
        win: Window,

    ) {
        super(playerName, playlist, config, factoryBuilder, win, unmuter)
        const steroid = PromiseOnSteroids.getResolvable<UnmuteInfo>();
        this.unmutePromise = steroid.promise;
        this.unmuter.onFirstCheck((e, info) => steroid.resolve(info));
        this.gui.setPlayerControls(this);
        this.currentContext = new Promise((resolve) => {
            this.firstContextResolve = resolve;
        });

        this.notStartedYet = true;

        this.futureAdController = adsControllerPromise.then(ads => ads.onStart().then(_ => ads));

        this.futureAdController.then(adcont => adcont.onAdEvent((evt) => {
            this.triggerEvent(evt);
        }));
    }



    public getCurrentVideoContext(): Promise<VideoContext> {
        return this.currentContext;
    }


    public pause(): Promise<boolean> {
        return this.currentContext.then(e => e.pause(MediaControlOrigin.USER)).then(_ => true);
    }


    public mute(enable?: boolean): Promise<boolean> {
        return this.currentContext.then(e => e.muted(enable));
    }


    protected functionalWrap(ctx: AdsEnrichedVideoContext): AdsEnrichedVideoContext {

        return new MegarideAdsEnrichedVideoContext(ctx);
    }


    private wrapContext(prvCtx: VideoContext, forceResume: boolean, willAutoplay: boolean): Promise<AdsEnrichedVideoContext> {

        const ctl = this.wrapMediaPlayerContext(prvCtx)

        const waitPromise = this.notStartedYet && this.currentContext ? this.currentContext.then(cnt => cnt.destroy()) : Promise.resolve();
        this.currentContext = waitPromise.then(_ => this.futureAdController.then(adcont => adcont.getAdAndWrapContext(ctl, willAutoplay))).
            then(ctl => this.functionalWrap(ctl));
        return this.currentContext.then(c => {
            this.gui.setVideoContext(c);
            c.on("ended", () => {
                if (!this.hasNext()) this.triggerMegaridePlaylistEvent(MegarideEventType.MEGARIDE_PLAYLIST_END);
            })
            return c;
        });
    }

    private prepareMediaContext(mediaIdOrIndex: MediaIdOrPlaylistIndex, forceResume: boolean, willAutoplay: boolean) {
        return this.extractMediaFromPlaylist(mediaIdOrIndex).then(info => {
            return this.unmutePromise.then(uinfo => {

                return this.buildMediaContext(info).then(newCtx => {

                    if (this.config.audioOnPolicy == MediaAudioOnPolicy.ON_IF_AVAILABLE_AT_START) {
                        console.log("[AGGRO] AT START", uinfo);
                        if (uinfo.unmutedAtStart) {
                            this.setGeneralMute(false).then(_ => this.unmuter.afterUnmute())
                        }
                    }
                    else if (this.config.audioOnPolicy == MediaAudioOnPolicy.ON_WHEN_AVAILABLE) {
                        // #13960
                        const deferredListener = (evt: Event, uinfo: UnmuteInfo) => {
                            if (isAnyUnmutable(uinfo) && !uinfo.gainRemoved) {
                                this.unmuter.offUnmuteConditionChange(deferredListener);
                                console.log("[AGGRO] AT RANDOMTIME", uinfo);
                                this.setGeneralMute(false).then(_ => this.unmuter.afterUnmute())
                            }
                        }

                        if (uinfo.unmutedAtStart) {
                            console.log("[AGGRO] AT START", uinfo);
                            this.setGeneralMute(false).then(_ => this.unmuter.afterUnmute())
                        } else if (!uinfo.gainRemoved)
                            this.unmuter.onUnmuteConditionChange(deferredListener)

                    }

                    return this.wrapContext(newCtx, forceResume, willAutoplay)
                });
            })
        });
    }

    public play(mediaIdOrIndex: MediaIdOrPlaylistIndex, forceResume: boolean): Promise<AdsEnrichedVideoContext> {
        const currentCtx = this.getCurrentPlayingMediaContext();
        if (currentCtx) currentCtx.destroy();
        return this.prepareMediaContext(mediaIdOrIndex, forceResume, true).then(c =>
            c.startWithAds(forceResume).then(_ => c)
        );
    }

    public fetchPlayerMediaElements(mediaID: MediaId): VideoPlayerElements {
        return this.gui.getPlayerMediaElementsForId(mediaID);
    }

    public clearPlayerMediaElement(mediaID: MediaId): void {

    }

    protected beforeResourceStart(ctx: VideoContext): void {
        ctx.muted(this.getGeneralMute());
    }

    public signalError(s: string): void {
        this.logger.error("error", s);
    }

    private triggerMegaridePlaylistEvent(evt: MegarideEventType) {

        const evtToTrigger: MegarideEvent = {
            eventType: evt,
            mediaId: this.playlist.currentMedia?.id ? this.playlist.currentMedia.id : "noid",
        }
        this.triggerEvent(evtToTrigger);
    }



    public start(): Promise<VideoContext> {
        return this.initPromise.then(c => {
            if (c.isPaused) {
                c.togglePlay(false);
            }

            return c;
        })
    }



    public init(): Promise<VideoContext> {
        if (this.notStartedYet) {
            this.notStartedYet = false;



            if (this.config.audioOnPolicy == MediaAudioOnPolicy.ON && (this.config.autoplay == MediaAutoplayPolicy.AUTOPLAY_ALWAYS ||
                this.config.autoplay == MediaAutoplayPolicy.AUTOPLAY_IF_ADS_ARE_SERVABLE)) {
                this.config.autoplay = MediaAutoplayPolicy.AUTOPLAY_ONLY_IF_AUDIO_ON;
            }


            let unmutePromise = Promise.resolve();
            //#12431 revisione unmute policy
            if (this.config.audioOnPolicy == MediaAudioOnPolicy.ON) {
                unmutePromise = this.setGeneralMute(false);
            }

            const willAutoplay: boolean = this.config.autoplay == MediaAutoplayPolicy.AUTOPLAY_ALWAYS ||
                this.config.autoplay == MediaAutoplayPolicy.AUTOPLAY_IF_ADS_ARE_SERVABLE;


            //?PERF: la prima riproduzione viene concatenata al caricamento di ima per prioritizzare quest'ultima
            this.initPromise = unmutePromise.then(_ =>
                this.futureAdController.then(adsReady => {

                    const promise = this.prepareMediaContext(this.playlist.currentMedia.id, false, willAutoplay).then(ctx => {
                        const run = () => {
                            //this.gui.loadingUpdate(true);
                            return ctx.startWithAds(false).then(_ => ctx);
                        }
                        if (this.config.autoplay == MediaAutoplayPolicy.AUTOPLAY_ALWAYS ||
                            (this.config.autoplay == MediaAutoplayPolicy.AUTOPLAY_IF_ADS_ARE_SERVABLE && ctx.isAdsEnriched)) {
                            return run();
                        }
                        else if (this.config.autoplay == MediaAutoplayPolicy.AUTOPLAY_ONLY_IF_AUDIO_ON) {
                            this.unmuter.whenIsFirstUnmutable(() => run());
                            return ctx;
                        }
                        else {
                            return ctx;
                        }
                    });

                    if (this.firstContextResolve) {
                        const r = this.firstContextResolve;
                        promise.then(x => r(x));
                        this.firstContextResolve = null;
                    }
                    this.triggerMegaridePlaylistEvent(MegarideEventType.MEGARIDE_PLAYLIST_START);
                    return promise;
                }))

            return this.initPromise;
        }
        else {
            return Promise.reject("Already Started");
        }
    }


    public hasPrevious(): boolean {
        return this.playlist.currentIndex != 0 && this.playlist.length > 1
    }

    public hasNext(): boolean {
        return this.playlist.currentIndex < this.playlist.length - 1
    }

    public playNext(forceResume: boolean): Promise<VideoContext> {
        return this.playlist.getNextMedia().then(nextVideo => {
            if (nextVideo)
                return this.play(nextVideo.id, forceResume);
            else {
                if (this.config.loopPlaylist) {
                    return this.restart(forceResume);
                } else {
                    return Promise.reject("No next video")
                }
            }
        })
    }

    public playPrevious(forceResume: boolean): Promise<VideoContext> {
        return this.playlist.getPrevMedia().then(prevVideo => {
            if (prevVideo)
                return this.play(prevVideo.id, forceResume);
            else {
                if (this.config.loopPlaylist) {
                    return this.restart(forceResume);
                } else {
                    return Promise.reject("No previous video")
                }
            }
        })
    }

    public restart(forceResume: boolean): Promise<VideoContext> {
        return this.playlist.gotoFirst().then(first => {
            if (first) {
                const currentCtx = this.getCurrentPlayingMediaContext();
                let promise = null;

                this.beforeResourceRestart(first.id)
                if (currentCtx.ref.info.id == first.id) {

                    promise = currentCtx.reset().then(_ => {
                        this.triggerMediaLoadedEvent(currentCtx, true);
                        return this.wrapContext(currentCtx, forceResume, true).then(c =>
                            c.startWithAds(forceResume).then(_ => c)
                        );
                    });
                } else {
                    promise = this.play(first.id, forceResume);
                }

                return promise.then(x => {
                    this.triggerMegaridePlaylistEvent(MegarideEventType.MEGARIDE_PLAYLIST_START);
                    return x;
                })
            } else {
                return Promise.reject("No video to play");
            }
        })
    }

    public toggleCast(evt: Event): Promise<void> {

        if (isCastablePlayerTechFactory(this.factory))
            return this.factory.toggleCast(evt)
        else
            return Promise.resolve()


    }

}
